import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { Subscription, SubscriptionLimits, subscriptionService, LifecycleState, Organization } from '../services/subscriptionService'
import { useAuth } from './AuthContext'
import { environmentService } from '../services/environmentService'
import { loggingService } from '../services/loggingService'

interface SubscriptionContextValue {
  subscription: Subscription | null
  limits: SubscriptionLimits | null
  loading: boolean
  isActive: boolean
  daysLeft: number | null
  lifecycleState: LifecycleState
  gracePeriodDaysLeft: number | null
  isReadOnly: boolean
  canReactivate: boolean
  refresh: () => Promise<void>
  canAccess: (feature: keyof SubscriptionLimits) => boolean
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [limits, setLimits] = useState<SubscriptionLimits | null>(null)
  const [loading, setLoading] = useState(true)
  const [lifecycleState, setLifecycleState] = useState<LifecycleState>('trialing')

  const orgId = organization?.id ?? null

  const refresh = useCallback(async () => {
    if (!orgId) { setLoading(false); return }
    setLoading(true)
    try {
      if (environmentService.shouldBypassSubscriptionChecks()) {
        loggingService.info('subscription', 'Developer mode: bypassing subscription checks', {
          environment: environmentService.getEnvironment(),
          orgId,
        });
        setLifecycleState('active');
        setSubscription({
          id: 'dev-mode-subscription',
          org_id: orgId,
          tier: 'enterprise',
          status: 'active',
          stripe_customer_id: null,
          stripe_subscription_id: null,
          trial_ends_at: null,
          trial_expired_at: null,
          grace_period_started_at: null,
          scheduled_tier: null,
          scheduled_tier_effective_date: null,
          next_billing_date: null,
          cancel_at_period_end: false,
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          canceled_at: null,
          last_payment_failed_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        setLimits({
          tier: 'enterprise',
          max_users: -1,
          max_projects: -1,
          max_resources: -1,
          has_sso: true,
          has_audit_log: true,
          has_partner_rates: true,
          has_revenue_forecast: true,
          has_gantt: true,
        });
      } else {
        const updatedState = await subscriptionService.checkAndUpdateLifecycleStatus(orgId)
        setLifecycleState(updatedState)

        const sub = await subscriptionService.getSubscription(orgId)
        console.log('[Subscription] Loaded subscription:', sub)
        setSubscription(sub)
        if (sub) {
          const lim = await subscriptionService.getLimits(sub.tier)
          console.log('[Subscription] Loaded limits for tier', sub.tier, ':', lim)
          setLimits(lim)
        } else {
          console.warn('[Subscription] No subscription found for organization', orgId)
        }
      }
    } catch (error) {
      console.error('[Subscription] Error loading subscription:', error)
    } finally { setLoading(false) }
  }, [orgId])

  useEffect(() => { refresh() }, [refresh])

  const isActive = environmentService.shouldBypassSubscriptionChecks() || subscriptionService.isActive(subscription)
  const daysLeft = environmentService.shouldBypassSubscriptionChecks() ? null : subscriptionService.getDaysLeft(subscription)
  const gracePeriodDaysLeft = environmentService.shouldBypassSubscriptionChecks() ? null : subscriptionService.getGracePeriodDaysLeft(organization as Organization | null)
  const isReadOnly = environmentService.shouldBypassSubscriptionChecks() ? false : subscriptionService.isReadOnly(lifecycleState)
  const canReactivate = environmentService.shouldBypassSubscriptionChecks() ? false : subscriptionService.canReactivate(lifecycleState, organization as Organization | null)

  const canAccess = (feature: keyof SubscriptionLimits): boolean => {
    if (loading) return true
    if (!limits) return true
    const val = limits[feature]
    if (typeof val === 'boolean') return val
    return true
  }

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      limits,
      loading,
      isActive,
      daysLeft,
      lifecycleState,
      gracePeriodDaysLeft,
      isReadOnly,
      canReactivate,
      refresh,
      canAccess
    }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider')
  return ctx
}
